import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Sparkles, Loader2, Check, AlertTriangle, Printer } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const issueOptions = [
  "Gaslighting", "Love bombing", "Silent treatment", "Financial control",
  "Isolation from family/friends", "Verbal abuse", "Physical abuse",
  "Cheating/infidelity", "Emotional manipulation", "Triangulation",
  "Coercive control", "Narcissistic rage", "DARVO", "Hoovering",
];

const RecoveryPlanPage = () => {
  const [name, setName] = useState("");
  const [relType, setRelType] = useState("");
  const [duration, setDuration] = useState("");
  const [issues, setIssues] = useState([]);
  const [feelings, setFeelings] = useState("");
  const [goals, setGoals] = useState("");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggleIssue = (issue) => {
    setIssues(prev => prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!name || !relType || !issues.length || !feelings || !goals) {
      setError("Please fill in all fields and select at least one issue.");
      return;
    }
    setError("");
    setLoading(true);
    setPlan("");
    try {
      const res = await axios.post(`${API}/recovery-plan/generate`, {
        name, relationship_type: relType, duration, main_issues: issues,
        current_feelings: feelings, goals,
      });
      setPlan(res.data.plan);
    } catch {
      setError("Unable to generate your plan right now. Please try again.");
    } finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="recovery-plan-section">
        <div className="container mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-6 text-sm"><ArrowLeft className="h-4 w-4" /> Back</Link>

          <div className="text-center mb-8">
            <Sparkles className="h-10 w-10 text-[#FFB7B2] mx-auto mb-4" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="plan-title">Your Personalized Recovery Plan</h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">Answer a few questions and our AI generates a custom 30-day recovery plan just for you. Print it, save it, follow it.</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">This plan is for educational purposes only. It is not therapy and does not promise to cure any disease or mental illness. Always consult professionals.</p>
          </div>

          {!plan ? (
            <Card className="rounded-2xl shadow-floating border-0" data-testid="plan-form">
              <CardContent className="p-8">
                <form onSubmit={handleGenerate} className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Your First Name *</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Brian" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="plan-name" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Relationship Type *</label>
                      <select value={relType} onChange={e => setRelType(e.target.value)} required className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="plan-type">
                        <option value="">Select</option>
                        <option value="romantic partner">Romantic Partner</option>
                        <option value="spouse">Spouse</option>
                        <option value="parent">Parent</option>
                        <option value="family member">Family Member</option>
                        <option value="workplace">Workplace</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">How Long Did It Last?</label>
                      <input type="text" value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g., 3 years" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="plan-duration" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A2A3A] mb-2">What Did You Experience? (Select all that apply) *</label>
                    <div className="flex flex-wrap gap-2">
                      {issueOptions.map(issue => (
                        <button key={issue} type="button" onClick={() => toggleIssue(issue)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${issues.includes(issue) ? "bg-[#4FB3D4] text-white" : "bg-white border border-[#E5E7EB] text-[#1A2A3A]/60 hover:border-[#4FB3D4]"}`} data-testid={`issue-${issue.toLowerCase().replace(/\s/g, '-')}`}>
                          {issues.includes(issue) && <Check className="h-3 w-3 inline mr-1" />}{issue}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A2A3A] mb-1">How Are You Feeling Right Now? *</label>
                    <textarea value={feelings} onChange={e => setFeelings(e.target.value)} required rows={3} placeholder="Angry, confused, hopeful, scared, numb... anything you feel is valid." className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none" data-testid="plan-feelings" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A2A3A] mb-1">What Are Your Recovery Goals? *</label>
                    <textarea value={goals} onChange={e => setGoals(e.target.value)} required rows={3} placeholder="Set boundaries, rebuild confidence, trust again, find peace..." className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none" data-testid="plan-goals" />
                  </div>
                  {error && <p className="text-red-500 text-sm" data-testid="plan-error">{error}</p>}
                  <Button type="submit" disabled={loading} className="w-full bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full py-5 font-semibold" data-testid="plan-generate-btn">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {loading ? "Generating Your Plan..." : "Generate My 30-Day Plan"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div data-testid="plan-result">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-xl font-semibold text-[#1A2A3A]">Your 30-Day Recovery Plan</h2>
                <Button onClick={handlePrint} variant="outline" className="rounded-full px-4 py-2 text-sm" data-testid="plan-print-btn">
                  <Printer className="h-4 w-4 mr-2" /> Print / Save
                </Button>
              </div>
              <Card className="rounded-2xl shadow-card border border-[#E5E7EB]/50">
                <CardContent className="p-8">
                  <div className="prose prose-sm max-w-none text-[#1A2A3A]/80 whitespace-pre-wrap leading-relaxed" data-testid="plan-content">{plan}</div>
                </CardContent>
              </Card>
              <div className="text-center mt-6">
                <Button onClick={() => setPlan("")} variant="outline" className="rounded-full px-6 py-3 text-sm" data-testid="plan-new-btn">Generate a New Plan</Button>
              </div>
            </div>
          )}
        </div>
      </section>
      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB)</p>
      </footer>
    </div>
  );
};

export default RecoveryPlanPage;
